<template>
  <div class="saved-hunts-map-view">
    <div class="header-container">
      <h2>Your Saved Hunts</h2>
    </div>
    
    <div v-if="pointsNotSynced" class="alert alert-warning">
      You haven't saved your draw points yet. Draw odds information on the map will by default assume 0 points.
    </div>
    
    <!-- Results wheel above the map -->
    <div class="results-section" v-if="searchResults && searchResults.length > 0">
      <div class="results-wheel-container">
        <HuntWheelSelector 
          :hunts="searchResults" 
          @hunt-selected="onHuntSelected"
          @toggle-bio-card="toggleBioCard"
        />
      </div>
    </div>
    
    <div class="results-section" v-else-if="searchPerformed && (!searchResults || searchResults.length === 0)">
      <p>No saved hunts found.</p>
    </div>
    
    <div v-if="error" class="alert alert-danger mt-3">
      {{ error }}
    </div>
    
    <!-- Draw Odds Result card above map when visible -->
    <div v-if="showBioCard && selectedHunt && drawOddsResult" :class="['bio-card-section', {'expanded': isCardExpanded}]">
      <DrawOddsResultCard 
        :result="drawOddsResult"
        @error="showError"
        @toggle-expanded="onCardExpandToggle"
      />
    </div>
    
    <!-- Loading state for draw odds -->
    <div v-if="showBioCard && selectedHunt && !drawOddsResult && isLoadingDrawOdds" class="loading-section">
      <p><span class="spinner-border spinner-border-sm" role="status"></span> Loading draw odds...</p>
    </div>

    <!-- Error Snackbar -->
    <v-snackbar
      v-model="showSnackbar"
      :color="snackbarColor"
      :timeout="3000"
    >
      {{ snackbarText }}
    </v-snackbar>
    
    <!-- Map container -->
    <div class="map-container">
      <ViewOnlyMap
        ref="mapRef"
      />
    </div>
  </div>
</template>

<script>
import { defineComponent } from 'vue'
import { fromEpsg3857ToLatLng } from '../utils/coordinateUtils'
import ViewOnlyMap from './ViewOnlyMap.vue'
import HuntWheelSelector from './HuntWheelSelector.vue'
import DrawOddsResultCard from './DrawOddsResultCard.vue'
import { mapGetters } from 'vuex'
import { apiToDrawPointsSpecies } from '../constants/species'

export default defineComponent({
  name: 'SavedHuntsMapView',
  components: {
    ViewOnlyMap,
    HuntWheelSelector,
    DrawOddsResultCard
  },
  props: {
    isUtahResident: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      searchResults: [],
      isLoading: false,
      searchPerformed: false,
      error: null,
      selectedHunt: null,
      huntBoundaryPolygon: null,
      showBioCard: false,
      // Draw odds related properties
      drawOddsResult: null,
      isLoadingDrawOdds: false,
      // Snackbar
      showSnackbar: false,
      snackbarText: '',
      snackbarColor: 'error',
      // Track expanded state of the DrawOddsResultCard
      isCardExpanded: false,
      // Current species from the selected hunt
      currentSpecies: "LIMITED ENTRY ELK", // Default species
    }
  },
  computed: {
    ...mapGetters(['getDrawPoints']),
    
    /**
     * Map API species to drawPoints species format
     * This dictionary maps from the API species format to user drawPoints species format
     */
    apiToDrawPointsSpecies() {
      return apiToDrawPointsSpecies;
    },
    
    userDrawPoints() {
      console.log("getDrawPoints", this.getDrawPoints);
      if (this.getDrawPoints === null) return 0;
      
      // If drawPoints is an array (like in ViewDrawOdds)
      if (Array.isArray(this.getDrawPoints)) {
        // Use the current species from the selected hunt
        const apiSpecies = this.currentSpecies;

        if (!apiSpecies) return 0;
        
        // Map API species to drawPoints species format
        const drawPointsSpecies = this.apiToDrawPointsSpecies[apiSpecies];
        
        // If we have a mapping, find the matching drawPoint
        if (drawPointsSpecies) {
          const match = this.getDrawPoints.find(dp => dp.species === drawPointsSpecies);
          return match ? match.points : 0;
        }
        
        // If no mapping exists, try direct match as fallback
        const directMatch = this.getDrawPoints.find(dp => dp.species === apiSpecies);
        return directMatch ? directMatch.points : 0;
      }
      
      // If it's a number, return it directly
      if (typeof this.getDrawPoints === 'number') {
        return this.getDrawPoints;
      }
      
      // Otherwise it's likely an object, so we need to extract just the points
      // This handles the case where drawPoints is the full object from the API
      return 0
    },
    pointsNotSynced() {
      return this.getDrawPoints === null || this.getDrawPoints.length === 0;
    }
  },
  mounted() {
    // Fetch saved hunts when component is mounted - we use nextTick to ensure
    // the component is fully mounted before emitting the loading-state-change event
    this.$nextTick(() => {
      this.fetchSavedHunts();
    });
  },
  methods: {
    onHuntSelected(hunt) {
      this.selectedHunt = hunt;
      this.displayHuntBoundary();
      // Set showBioCard to false whenever a new hunt is selected
      this.showBioCard = false;
      console.log("Selected hunt:", hunt);
      if (hunt.stateAndSpecies) {
        // If stateAndSpecies exists and has the format "UT#SPECIES", extract SPECIES
        const parts = hunt.stateAndSpecies.split("#");
        if (parts.length > 1) {
          this.currentSpecies = parts[1];
        }
      }
      
      // The following is no longer needed since showBioCard is now false
      // If bio card is visible, fetch draw odds for the selected hunt
      if (this.showBioCard) {
        this.fetchDrawOddsForHunt(hunt);
      }
    },
    
    toggleBioCard() {
      this.showBioCard = !this.showBioCard;
      
      // If opening the bio card, fetch draw odds for the selected hunt
      if (this.showBioCard && this.selectedHunt) {
        this.fetchDrawOddsForHunt(this.selectedHunt);
      }
    },
    
    onCardExpandToggle(isExpanded) {
      this.isCardExpanded = isExpanded;
    },
    
    async fetchDrawOddsForHunt(hunt) {
      if (!hunt || !hunt.huntApplyId) return;
      
      this.isLoadingDrawOdds = true;
      this.drawOddsResult = null;
      
      try {
        // Ensure bonusPoints is a number
        let bonusPoints = 0;
        if (typeof this.userDrawPoints === 'number') {
          bonusPoints = this.userDrawPoints;
        }
        
        const queryParams = new URLSearchParams({
          species: this.currentSpecies,
          year: "2024",
          isResident: this.isUtahResident,
          bonusPoints: bonusPoints,
          huntApplyId: hunt.huntApplyId
        });

        const response = await fetch(
          `${this.$API_BASE_URL}/api/drawodds/species?${queryParams}`,
          {
            method: "GET",
            credentials: "include",
          }
        );

        if (response.ok) {
          const results = await response.json();
          // Should return just the one hunt we requested
          if (results && results.length > 0) {
            this.drawOddsResult = results[0];
          } else {
            console.log("No draw odds found for this hunt");
            this.showError("No draw odds found for this hunt");
          }
        } else if (response.status === 403) {
          console.log("Invalid session detected for draw odds");
          this.showError("Session expired. Please sign in again.");
        } else {
          console.log("Error fetching draw odds:", response.status);
          this.showError("Failed to fetch draw odds. Please try again.");
        }
      } catch (error) {
        console.error("Error fetching draw odds:", error);
        this.showError("Network error when fetching draw odds. Please try again.");
      } finally {
        this.isLoadingDrawOdds = false;
      }
    },

    showError(message) {
      this.snackbarText = message;
      this.snackbarColor = 'error';
      this.showSnackbar = true;
    },

    displayHuntBoundary() {
      if (!this.selectedHunt || !this.selectedHunt.rings || this.selectedHunt.rings.length === 0) {
        this.huntBoundaryPolygon = null;
        return;
      }

      try {
        // Process all rings from the selected hunt
        const polygonPaths = this.selectedHunt.rings.map(ring => {
          if (!ring || !Array.isArray(ring)) {
            console.error('Invalid ring format:', ring);
            return [];
          }

          // Convert EPSG:3857 points to lat/lng for the map
          return ring.map(point => {
            // Each point is a [x, y] array in EPSG:3857
            if (!Array.isArray(point) || point.length < 2) {
              console.error('Invalid point format:', point);
              return null;
            }
            
            return fromEpsg3857ToLatLng(point[0], point[1]);
          }).filter(point => point !== null);
        }).filter(ring => ring.length >= 3); // Only keep rings with at least 3 points

        if (polygonPaths.length > 0 && polygonPaths[0].length >= 3) {
          // Update the map with the new polygon (outer boundary + holes)
          if (this.$refs.mapRef) {
            this.$refs.mapRef.displayPolygon(polygonPaths);
          }
        } else {
          console.error('Not enough valid points to form a polygon');
        }
      } catch (error) {
        console.error('Error displaying hunt boundary:', error);
      }
    },

    async fetchSavedHunts() {
      this.isLoading = true;
      // Emit the loading started event to notify parent
      this.$emit('loading-state-change', true);
      this.error = null;
      this.searchPerformed = false;
      this.searchResults = [];

      try {
        const response = await fetch(
          this.$API_BASE_URL + '/api/hunt-map/get-saved-hunts',
          {
            method: 'GET',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include'
          }
        );
          console.log("status", response.status)
        if (response.ok && response.status !== 204 && response.status !== 403) {
          this.searchResults = await response.json();
          this.searchPerformed = true;
        } else if (response.status === 204) {
          this.searchResults = [];
          this.searchPerformed = true;
        } else if (response.status === 403) {
          // Handle 403 Forbidden (session expired/invalid)
          // Find the parent SearchMapExample component and call its showSignIn method
          let parent = this.$parent;
          while (parent && parent.$options.name !== 'SearchMapExample') {
            parent = parent.$parent;
          }
          
          if (parent && typeof parent.showSignIn === 'function') {
            parent.showSignIn();
          } else {
            // Fallback if parent not found
            this.$router.push('/?signin=true');
            setTimeout(() => {
              alert('Your login session is invalid or has expired. Please sign in again.');
            }, 100);
          }
          return;
        } else {
          const errorData = await response.json().catch(() => null);
          this.error = errorData?.message || 'Failed to fetch saved hunts. Please try again.';
        }
      } catch (err) {
        console.error('Error fetching saved hunts:', err);
        
        // For "Failed to fetch" errors, assume it might be a session issue
        // This is a common error when CORS blocks the request due to invalid credentials
        if (err.name === 'TypeError' && err.message === 'Failed to fetch') {
          // Find the parent SearchMapExample component and call its showSignIn method
          let parent = this.$parent;
          while (parent && parent.$options.name !== 'SearchMapExample') {
            parent = parent.$parent;
          }
          
          if (parent && typeof parent.showSignIn === 'function') {
            parent.showSignIn();
          } else {
            // Fallback if parent not found
            this.$router.push('/?signin=true');
            setTimeout(() => {
              alert('Your login session may be invalid or has expired. Please sign in again.');
            }, 100);
          }
          return;
        } else if (err.response && err.response.status === 403) {
          // Handle explicit 403 Forbidden (session expired/invalid)
          // Find the parent SearchMapExample component and call its showSignIn method
          let parent = this.$parent;
          while (parent && parent.$options.name !== 'SearchMapExample') {
            parent = parent.$parent;
          }
          
          if (parent && typeof parent.showSignIn === 'function') {
            parent.showSignIn();
          } else {
            // Fallback if parent not found
            this.$router.push('/?signin=true');
            setTimeout(() => {
              alert('Your login session is invalid or has expired. Please sign in again.');
            }, 100);
          }
          return;
        } else {
          this.error = 'Network error. Please try again.';
        }
      } finally {
        this.isLoading = false;
        // Emit that loading has finished
        this.$emit('loading-state-change', false);
        
        // Scroll to results
        if (this.searchPerformed && this.searchResults.length > 0) {
          this.$nextTick(() => {
            const resultsElement = document.querySelector('.wheel-picker-container') ||
                                   document.querySelector('.mt-4');
            if (resultsElement) {
              resultsElement.scrollIntoView({ behavior: 'smooth' });
            }
          });
        }
      }
    }
  }
})
</script>

<style scoped>
.saved-hunts-map-view {
  max-width: 1400px; /* Increased max-width for larger screens */
  margin: 0 auto;
  padding: 1rem;
}

.header-container {
  display: flex;
  align-items: center;
  margin-bottom: 1rem;
  position: relative;
}

.header-container h2 {
  flex: 1;
  text-align: center;
  margin-top: 1rem;
}

.map-controls {
  display: flex;
  justify-content: center;
  margin-bottom: 1rem;
}

.button-group {
  display: flex;
  gap: 0.5rem;
}

/* New styles for the updated layout */
.results-section {
  margin-bottom: 1.5rem;
}

.results-wheel-container {
  max-width: 600px;
  margin: 0 auto;
  height: auto;
}

.bio-card-section,
.loading-section {
  max-width: 800px;
  margin: 0 auto 1.5rem;
  overflow-y: auto;
  max-height: 300px;
  border: 1px solid #ddd;
  border-radius: 4px;
  padding: 1rem;
  background-color: #f8f9fa;
  transition: max-height 0.3s ease-in-out;
}

/* Double the height when expanded */
.bio-card-section.expanded {
  max-height: 600px;
}

.loading-section {
  text-align: center;
  padding: 2rem;
}

.map-container {
  width: 100%;
  height: 600px;
  border: 1px solid #ddd;
  border-radius: 4px;
  overflow: hidden;
}

.spinner-border {
  vertical-align: middle;
}

.alert {
  margin: 1rem 0;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .bio-card-section {
    max-height: 250px;
  }
  
  /* Double the height when expanded on mobile */
  .bio-card-section.expanded {
    max-height: 500px;
  }
  
  .map-container {
    height: 500px; /* Smaller map on mobile */
  }
  
  .results-wheel-container {
    max-width: 100%;
  }
}
</style>
